import React, { forwardRef } from "react";
import { Check, Minus } from "lucide-react";
import { cn } from "../../lib/utils.js";
import { mergeRefs } from "../../primitives/index.js";
import { useFormField } from "../../lib/form-field-context.js";
import {
  CHOICE_CARD_LABEL,
  CHOICE_CARD_TRAILING,
  CHOICE_DETAIL,
  CHOICE_ROW,
  CHOICE_TEXT,
  CHOICE_TRAILING,
  choiceCardRow,
  type ChoiceVariant,
} from "../../lib/choice-card.js";

/* ---------------- Checkbox ----------------
   forwardRef targets the native <input>; it is merged with the internal ref
   used to drive the `indeterminate` DOM property. For unlabeled uses (table
   select column, etc.) pass `aria-label`/`aria-labelledby` so the control
   still has an accessible name. */

/** `card` is a bordered, full-width row at least 44px tall; the checked row
 *  takes the selected paint Card uses. */
export type CheckboxVariant = ChoiceVariant;

export interface CheckboxProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "onChange" | "checked" | "defaultChecked" | "type"
> {
  checked?: boolean;
  defaultChecked?: boolean;
  indeterminate?: boolean;
  onChange?: (checked: boolean) => void;
  label?: React.ReactNode;
  /** Visual style of the row. Default `default`. */
  variant?: CheckboxVariant;
  /** Secondary text under the label. Linked with aria-describedby; never
   *  part of the accessible name. */
  description?: React.ReactNode;
  /** Content at the row's end edge, OUTSIDE the label, so a button here is
   *  never nested inside the checkbox's label. */
  trailing?: React.ReactNode;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  function Checkbox(
    {
      checked,
      defaultChecked,
      indeterminate,
      onChange,
      label,
      variant = "default",
      description,
      trailing,
      disabled,
      id,
      className,
      "aria-describedby": ariaDescribedby,
      "aria-invalid": ariaInvalid,
      "aria-required": ariaRequired,
      ...rest
    },
    forwardedRef,
  ) {
    const autoId = React.useId();
    const cid = id ?? autoId;
    const innerRef = React.useRef<HTMLInputElement>(null);
    React.useEffect(() => {
      if (innerRef.current) innerRef.current.indeterminate = !!indeterminate;
    }, [indeterminate]);
    const isControlled = checked !== undefined;
    const [internal, setInternal] = React.useState(!!defaultChecked);
    const value = isControlled ? !!checked : internal;
    const field = useFormField();
    const Mark = indeterminate ? Minus : Check;
    const card = variant === "card";
    // With a trailing slot or the card row the label is no longer the root,
    // so className (layout only) moves to the row that is.
    const row = card || Boolean(trailing);
    // The description sits INSIDE the label so the whole row toggles, so the
    // box takes its name from the label text alone, unless the caller names it.
    const labelId = `${cid}-label`;
    const descriptionId = `${cid}-description`;
    const namedByLabelText =
      Boolean(description) &&
      Boolean(label) &&
      rest["aria-label"] === undefined &&
      rest["aria-labelledby"] === undefined;
    const describedBy = field?.describedBy ?? ariaDescribedby;
    const labelText = label && (
      <span
        id={namedByLabelText ? labelId : undefined}
        className="text-sm text-pb-text"
      >
        {label}
      </span>
    );
    const labelElement = (
      <label
        htmlFor={cid}
        className={cn(
          card
            ? [CHOICE_CARD_LABEL, "cursor-pointer"]
            : "inline-flex items-center gap-2 cursor-pointer select-none",
          disabled && "opacity-50 cursor-not-allowed",
          !row && className,
        )}
      >
        <span className="relative inline-flex">
          <input
            {...(namedByLabelText ? { "aria-labelledby": labelId } : {})}
            {...rest}
            ref={mergeRefs(innerRef, forwardedRef)}
            id={cid}
            type="checkbox"
            checked={value}
            disabled={disabled}
            aria-describedby={
              description
                ? [describedBy, descriptionId].filter(Boolean).join(" ")
                : describedBy
            }
            aria-invalid={field?.invalid ?? ariaInvalid}
            aria-required={field?.required ?? ariaRequired}
            onChange={(e) => {
              if (!isControlled) setInternal(e.target.checked);
              onChange?.(e.target.checked);
            }}
            className="peer sr-only"
          />
          <span
            className={cn(
              "w-4 h-4 rounded border flex items-center justify-center transition-colors",
              value || indeterminate
                ? "bg-pb-teal border-pb-teal text-pb-on-accent"
                : field?.invalid
                  ? "bg-pb-card border-pb-red"
                  : "bg-pb-card border-pb-control-border",
              "peer-focus-visible:ring-2 peer-focus-visible:ring-pb-teal/40",
            )}
          >
            {/* ADR-0091: the mark is ALWAYS rendered so it has something to
                animate from — the box fades its colour over the state duration,
                and a glyph that appears in one frame reads as a flicker against
                it. `pb-control-mark` fades and scales it with the box. */}
            <Mark
              aria-hidden="true"
              data-checked={value || indeterminate ? "true" : "false"}
              className="pb-control-mark w-3 h-3"
              strokeWidth={3}
            />
          </span>
        </span>
        {description ? (
          <span className={CHOICE_TEXT}>
            {labelText}
            <span id={descriptionId} className={CHOICE_DETAIL}>
              {description}
            </span>
          </span>
        ) : (
          labelText
        )}
      </label>
    );
    if (!row) return labelElement;
    // The trailing slot sits beside the label, never inside it.
    return (
      <div
        className={cn(
          card ? choiceCardRow(value, !!disabled) : CHOICE_ROW,
          className,
        )}
      >
        {labelElement}
        {trailing && (
          <span className={card ? CHOICE_CARD_TRAILING : CHOICE_TRAILING}>
            {trailing}
          </span>
        )}
      </div>
    );
  },
);
